// Phase 999.4 Plan 07 — Pure wizard reducer + helpers (R6, R8, R10).
//
// Per CONTEXT.md D-10: a single CreateSiteWizard component holds wizard state in
// useReducer; sub-components receive (state, dispatch) via props. NO React Context.
// Per SPEC R8: wizard state lives in component memory only — refresh / tab close
// discards automatically; no DB writes until Step E Confirm. This module is pure
// (zero React imports, zero fetch) so it can be unit-tested in isolation and so
// future tooling (e.g. wizard state migrations, replay-from-log) can consume it
// without dragging React into the dependency graph.
//
// Pitfall #4 reminder: this file MUST NOT register a unload-event listener.
// D-12's three rails (Cancel button + react-router-dom v5 <Prompt> + refresh-via-
// memory-discard) are sufficient and live in CreateSiteWizard.js, not here.

export const STEPS = ['A', 'B', 'C', 'D', 'E'];

// initialState shape:
//   org: existing-org pick uses {id, name, description}; new-org create uses
//        {id: null, name, description}. The id===null discriminates the two.
//   orglocation: name + description + utmJson (string; parsed at validate time
//                AND at server time — D-08 / Plan 04 stores the string verbatim).
//   hardware: array of {name, description, collisionError?}. collisionError is
//             a string when StepC's debounced check-name returned taken=true;
//             null when the latest probe came back clear.
//   optionOverrides: { [optionTypeId]: value-string } map. Empty/cleared overrides
//                    are removed via CLEAR_OPTION so buildPayloadFromState only
//                    emits actual overrides (R6 acceptance: "only the overridden
//                    option, not every default").
//   submitting / errors / result: Step E POST lifecycle markers.
export const initialState = {
    step: 'A',
    org: { id: null, name: '', description: '' },
    orglocation: { name: '', description: '', utmJson: '{}' },
    hardware: [],
    optionOverrides: {},
    errors: {},
    submitting: false,
    result: null,
};

export function nextStep(s) {
    const i = STEPS.indexOf(s);
    return i >= 0 && i < STEPS.length - 1 ? STEPS[i + 1] : s;
}

export function prevStep(s) {
    const i = STEPS.indexOf(s);
    return i > 0 ? STEPS[i - 1] : s;
}

export function wizardReducer(state, action) {
    switch (action.type) {
        case 'SET_ORG':
            return { ...state, org: action.org };
        case 'SET_LOCATION':
            return { ...state, orglocation: action.location };
        case 'ADD_HARDWARE':
            return { ...state, hardware: [...state.hardware, { name: '', description: '' }] };
        case 'UPDATE_HARDWARE':
            return {
                ...state,
                hardware: state.hardware.map((h, i) => (i === action.idx ? { ...h, ...action.patch } : h)),
            };
        case 'REMOVE_HARDWARE':
            return { ...state, hardware: state.hardware.filter((_, i) => i !== action.idx) };
        case 'SET_OPTION':
            return {
                ...state,
                optionOverrides: { ...state.optionOverrides, [action.optionTypeId]: action.value },
            };
        case 'CLEAR_OPTION': {
            const next = { ...state.optionOverrides };
            delete next[action.optionTypeId];
            return { ...state, optionOverrides: next };
        }
        case 'NEXT':
            return { ...state, step: nextStep(state.step) };
        case 'PREV':
            return { ...state, step: prevStep(state.step) };
        case 'GOTO':
            return { ...state, step: action.step };
        case 'SUBMIT':
            return { ...state, submitting: true, errors: { ...state.errors, submit: undefined } };
        case 'SUCCESS':
            return { ...state, submitting: false, result: action.result };
        case 'FAIL':
            return { ...state, submitting: false, errors: { ...state.errors, submit: action.error } };
        case 'RESET':
            return initialState;
        default:
            return state;
    }
}

// Maps wizard state to the AdminCreateSitePayload shape POSTed to /api/admin/sites
// (Plan 04 contract, Plan 02 DTO). Notes:
//   - isNewOrg/existingOrgId/orgName/orgDescription form a discriminated union:
//     when org.id is null we are creating a new org (orgName + orgDescription),
//     otherwise we are reusing existingOrgId.
//   - hardware emits {name, description} pairs only — collisionError stays in the
//     UI layer, the server enforces uniqueness atomically via the transaction.
//   - optionOverrides emits ONLY the overridden options per R6 acceptance:
//     "only the overridden option, not every default". CLEAR_OPTION removes
//     blanked entries from the map so they never appear in this payload.
export function buildPayloadFromState(state) {
    return {
        isNewOrg: state.org.id == null,
        existingOrgId: state.org.id == null ? undefined : state.org.id,
        orgName: state.org.id == null ? state.org.name : undefined,
        orgDescription: state.org.id == null ? (state.org.description || '') : undefined,
        locationName: state.orglocation.name,
        locationDescription: state.orglocation.description || '',
        utmJson: state.orglocation.utmJson || '{}',
        hardware: state.hardware.map((h) => ({ name: h.name, description: h.description || '' })),
        optionOverrides: Object.entries(state.optionOverrides).map(([k, v]) => ({
            optionTypeId: parseInt(k, 10),
            value: v == null ? '' : v,
        })),
    };
}

export default wizardReducer;
